import { useEffect, useState, useCallback, RefObject } from 'react'
import { sanitizeIntersectionThreshold } from '@/utils/intersectionObserver'

const useIntersection = ({
  parentRef,
  threshold = 0,
  rootMargin = '0px',
  cb,
}: {
  parentRef: RefObject<Element> | null
  threshold?: number | number[]
  rootMargin?: string
  cb?: (isIntersecting: boolean) => void
}) => {
  const [isIntersecting, setIntersecting] = useState<boolean>(false)
  const [childNode, setChildNode] = useState<Element | null>(null)

  const childRef = useCallback((node: Element | null) => {
    setChildNode(node)
  }, [])

  useEffect(() => {
    if (!childNode) return

    const safeThreshold = sanitizeIntersectionThreshold(threshold)

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIntersecting(entry.isIntersecting)
        if (cb) cb(entry.isIntersecting)
      },
      {
        root: parentRef?.current || null,
        threshold: safeThreshold,
        rootMargin,
      }
    )

    observer.observe(childNode)

    return () => {
      observer.unobserve(childNode)
      observer.disconnect()
    }
  }, [childNode, parentRef, threshold, rootMargin, cb])

  return { isIntersecting, childRef }
}

export default useIntersection
